import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../Layouts/Header';
import Footer from '../Layouts/Footer';
import { FiChevronDown, FiStar, FiChevronLeft, FiChevronRight, FiShoppingBag, FiTruck, FiShield, FiRefreshCw } from 'react-icons/fi';
import EcommerceBestSellerImg from '../Assets/Images/EcommerceBestSeller.png';
import ShopPageImg from '../Assets/Images/ShopPageImg.png';
import EcommerceorderTrackImg from '../Assets/Images/Ecommerce-ordertracking.png';
import EcommerceProductDetailsImg from '../Assets/Images/Ecommerce-ProductDetails.png';
import EcommerceCartImg from '../Assets/Images/Ecommerce-CartImg.png';

/* ── Scrolling Ticker ── */
const Ticker = () => {
  const items = ['NEW ARRIVALS', 'FREE SHIPPING ON ORDERS ₹1999+', 'SUMMER EDIT 2025', 'EXCLUSIVE MEMBER DISCOUNTS', 'EASY 30-DAY RETURNS', 'OVER 10,000 STYLES'];
  const doubled = [...items, ...items];
  return (
    <div className="overflow-hidden bg-black text-white py-2.5">
      <motion.div
        className="flex gap-10 whitespace-nowrap"
        animate={{ x: ['0%', '-50%'] }}
        transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
      >
        {doubled.map((item, i) => (
          <span key={i} className="text-[10px] sm:text-xs font-medium tracking-[0.2em] uppercase flex items-center gap-3">
            <span className="text-rose-400">✦</span> {item}
          </span>
        ))}
      </motion.div>
    </div>
  );
};

/* ── Premium App Carousel ── */
const slides = [
  {
    img: EcommerceBestSellerImg,
    label: 'Best Sellers',
    caption: 'Curated top-selling products, front and centre.',
    color: 'from-rose-600 to-pink-700',
  },
  {
    img: ShopPageImg,
    label: 'Shop Page',
    caption: 'Filter, sort and discover — an intuitive store experience.',
    color: 'from-violet-600 to-indigo-700',
  },
  {
    img: EcommerceorderTrackImg,
    label: 'Order Tracking',
    caption: 'Real-time delivery updates keep customers in the loop.',
    color: 'from-emerald-600 to-teal-700',
  },
  {
    img: EcommerceProductDetailsImg,
    label: 'Product Details',
    caption: 'Rich product pages with variants, images & reviews.',
    color: 'from-amber-500 to-orange-600',
  },
  {
    img: EcommerceCartImg,
    label: 'Cart & Checkout',
    caption: 'Frictionless cart experience built to convert.',
    color: 'from-sky-600 to-blue-700',
  },
];

const AppCarousel = () => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  const go = useCallback((idx) => {
    setDirection(idx > current ? 1 : -1);
    setCurrent(idx);
  }, [current]);

  const prev = () => go(current === 0 ? slides.length - 1 : current - 1);
  const next = () => go(current === slides.length - 1 ? 0 : current + 1);

  useEffect(() => {
    const t = setInterval(() => {
      setDirection(1);
      setCurrent(c => (c === slides.length - 1 ? 0 : c + 1));
    }, 4500);
    return () => clearInterval(t);
  }, []);

  const slide = slides[current];

  const variants = {
    enter: (d) => ({ opacity: 0, x: d > 0 ? 60 : -60 }),
    center: { opacity: 1, x: 0 },
    exit: (d) => ({ opacity: 0, x: d > 0 ? -60 : 60 }),
  };

  return (
    <section className="bg-white py-12 sm:py-20 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14">
          <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-rose-500 mb-3">Platform Preview</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium text-gray-900 leading-tight">
            Your store, <span className="text-rose-500">beautifully built.</span>
          </h2>
          <p className="text-gray-500 mt-3 text-sm sm:text-base max-w-lg mx-auto">
            Every screen crafted for conversion. Explore the experience your customers will love.
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Tab pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {slides.map((s, i) => (
              <button
                key={i}
                onClick={() => go(i)}
                className={`px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 ${
                  i === current
                    ? `bg-gradient-to-r ${s.color} text-white shadow-lg scale-105`
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Main card */}
          <div className={`relative rounded-2xl sm:rounded-3xl overflow-hidden bg-gradient-to-br ${slide.color} p-1 shadow-2xl`}>
            <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden">
              {/* Caption bar */}
              <div className={`bg-gradient-to-r ${slide.color} px-5 py-3 flex items-center justify-between`}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={current}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center gap-3"
                  >
                    <span className="text-white font-semibold text-sm sm:text-base">{slide.label}</span>
                    <span className="hidden sm:block text-white/70 text-xs">— {slide.caption}</span>
                  </motion.div>
                </AnimatePresence>
                <div className="flex items-center gap-3">
                  <button onClick={prev} className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-colors">
                    <FiChevronLeft size={16} />
                  </button>
                  <button onClick={next} className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-colors">
                    <FiChevronRight size={16} />
                  </button>
                </div>
              </div>

              {/* Image */}
              <div className="relative overflow-hidden" style={{ minHeight: '220px' }}>
                <AnimatePresence custom={direction} mode="wait">
                  <motion.img
                    key={current}
                    custom={direction}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.45, ease: 'easeInOut' }}
                    src={slide.img}
                    alt={slide.label}
                    className="w-full h-auto object-contain block"
                  />
                </AnimatePresence>
              </div>

              {/* Caption on mobile */}
              <div className="sm:hidden px-4 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-500 text-center">
                {slide.caption}
              </div>
            </div>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => go(i)}
                className={`rounded-full transition-all duration-300 ${
                  i === current ? 'w-6 h-2 bg-rose-500' : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── FAQ ── */
const Faq = ({ faqs }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-3">
      {faqs.map((f, i) => (
        <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden">
          <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-4 sm:p-5 text-left bg-white hover:bg-stone-50 transition-colors">
            <span className="font-medium text-gray-900 text-sm sm:text-base pr-4">{f.q}</span>
            <FiChevronDown className={`flex-shrink-0 transition-transform duration-300 ${open === i ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {open === i && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                <p className="p-4 sm:p-5 pt-2 text-gray-600 text-sm border-t border-gray-100">{f.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════ MAIN PAGE ═══════════════════════════════════════ */
export default function ClothingEcommerce() {
  useEffect(() => window.scrollTo(0, 0), []);

  const features = [
    { icon: <FiShoppingBag />, label: '10,000+ Styles', sub: 'Massive catalog ready to sell' },
    { icon: <FiTruck />, label: 'Fast Delivery', sub: 'Pan-India logistics network' },
    { icon: <FiShield />, label: 'Secure Checkout', sub: 'PCI-DSS compliant payments' },
    { icon: <FiRefreshCw />, label: '30-Day Returns', sub: 'Hassle-free return policy' },
  ];

  const faqs = [
    { q: 'Can I manage size & colour variants?', a: 'Yes — create infinite matrix variants under a single SKU, each with its own stock and price.' },
    { q: 'Do you support Instagram and Facebook shops?', a: 'Yes, sync your catalog and tag products in posts directly from the dashboard.' },
    { q: 'What payment gateways are available?', a: 'Razorpay, Stripe, PayPal, UPI, Apple Pay, Google Pay and 50+ more.' },
    { q: 'Is there built-in email marketing?', a: 'Yes — abandoned cart emails, win-back sequences, and promotional campaigns are all built in.' },
  ];

  const reviews = [
    { name: 'Priya M.', brand: 'House of Silks', text: "Switched from Shopify and tripled our mobile conversion in 2 months.", stars: 5, img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&q=80' },
    { name: 'Arjun R.', brand: 'UrbanThread', text: "The inventory matrix handles our 3000 SKU catalog flawlessly.", stars: 5, img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&q=80' },
    { name: 'Sneha K.', brand: 'Luxe Labels', text: "Customer support is incredible. Onboarded our entire catalog in 48 hours.", stars: 5, img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&q=80' },
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      <div className="bg-black text-white"><Header /></div>

      {/* ── TICKER ── */}
      <Ticker />

      {/* ── HERO ── */}
      <section className="relative bg-stone-950 overflow-hidden">
        {/* Background glows */}
        <div className="absolute top-0 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-rose-600/20 blur-[100px] rounded-full pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-48 sm:w-72 h-48 sm:h-72 bg-pink-600/15 blur-[80px] rounded-full pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-20 sm:pt-32 pb-16 sm:pb-24 text-center">
          {/* Badge */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6 sm:mb-8">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
            <span className="text-white/70 text-[10px] sm:text-xs font-medium uppercase tracking-widest">The New Standard for Fashion Commerce</span>
          </motion.div>

          {/* Headline */}
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="text-5xl sm:text-7xl md:text-8xl lg:text-[9rem] font-medium text-white leading-[0.88] tracking-tighter mb-6 sm:mb-8">
            SELL{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400">
              SMARTER.
            </span>
          </motion.h1>

          {/* Sub-heading */}
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="text-base sm:text-xl md:text-2xl text-stone-400 mb-10 sm:mb-12 max-w-xl sm:max-w-2xl mx-auto leading-relaxed font-light">
            Launch your apparel brand's online store in minutes. Built for speed, crafted for conversion, loved by fashion founders.
          </motion.p>

          {/* CTAs */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link target="_blank" rel="noopener noreferrer" to="http://103.61.224.178:8045/">
              <button className="w-full sm:w-auto bg-white text-black font-semibold px-8 sm:px-10 py-4 sm:py-5 rounded-full hover:bg-rose-500 hover:text-white transition-all tracking-wide text-sm shadow-[0_0_40px_rgba(255,255,255,0.15)]">
                START BUILDING →
              </button>
            </Link>
            <Link to="/products#DemoForm">
              <button className="w-full sm:w-auto border border-white/20 text-white font-medium px-8 sm:px-10 py-4 sm:py-5 rounded-full hover:bg-white/10 transition-all tracking-wide text-sm backdrop-blur-sm">
                WATCH DEMO
              </button>
            </Link>
          </motion.div>

          {/* Feature pills */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.5 }}
            className="flex flex-wrap justify-center gap-3 mt-10 sm:mt-14">
            {features.map((f, i) => (
              <div key={i} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2">
                <span className="text-rose-400 text-sm">{f.icon}</span>
                <span className="text-white/80 text-xs font-medium">{f.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── SUB-HERO BANNER ── */}
      <section className="bg-gradient-to-r from-rose-600 to-pink-600 py-8 sm:py-10 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-5 text-center sm:text-left">
          <div>
            <p className="text-rose-200 text-xs uppercase tracking-widest mb-1">Your brand. Your platform.</p>
            <h2 className="text-white text-2xl sm:text-3xl font-medium">The ecommerce engine<br className="hidden sm:block" /> built for fashion brands.</h2>
          </div>
          <div className="flex flex-col xs:flex-row gap-3 w-full sm:w-auto">
            <Link target="_blank" rel="noopener noreferrer" to="http://103.61.224.178:8045/">
              <button className="w-full sm:w-auto bg-white text-rose-600 font-semibold px-7 py-3.5 rounded-full hover:bg-rose-50 transition-all text-sm tracking-wider shadow-lg">
                START SELLING
              </button>
            </Link>
            <Link to="/products#DemoForm">
              <button className="w-full sm:w-auto border border-white/40 text-white font-medium px-7 py-3.5 rounded-full hover:bg-white/10 transition-all text-sm tracking-wider">
                WATCH DEMO
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── APP CAROUSEL ── */}
      <AppCarousel />

      {/* ── EDITORIAL CALLOUT ── */}
      <section className="flex flex-col md:flex-row overflow-hidden">
        <div className="w-full md:w-1/2 relative min-h-[260px] md:min-h-[500px]">
          <img src="https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=900&q=80" alt="Fashion" className="w-full h-full object-cover absolute inset-0" />
        </div>
        <div className="w-full md:w-1/2 bg-rose-500 text-white flex flex-col justify-center px-8 sm:px-12 md:px-16 py-14 md:py-20">
          <span className="text-rose-200 text-xs font-medium uppercase tracking-[0.3em] mb-5">Built for Scale</span>
          <h2 className="text-4xl sm:text-5xl font-medium leading-tight mb-5">Sell to 130+<br />countries. In<br />their currency.</h2>
          <p className="text-rose-100 text-base sm:text-lg mb-8 sm:mb-10 leading-relaxed max-w-md">Multi-currency checkout, automatic tax calculation, and localised payment methods — go global in a single click.</p>
          <button className="bg-black text-white font-medium px-8 sm:px-10 py-4 rounded-full self-start hover:bg-gray-900 transition-all text-sm sm:text-base">Explore Global Selling →</button>
        </div>
      </section>

      {/* ── REVIEW GRID ── */}
      <section className="py-16 sm:py-20 bg-white px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-medium text-center mb-10 sm:mb-14">Loved by <span className="text-rose-500">fashion founders.</span></h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {reviews.map((r, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-stone-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-stone-200">
                <div className="flex gap-1 mb-4">
                  {Array(r.stars).fill(0).map((_, j) => <FiStar key={j} className="text-amber-400 fill-amber-400" size={14} />)}
                </div>
                <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-5">"{r.text}"</p>
                <div className="flex items-center gap-3">
                  <img src={r.img} alt={r.name} className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover" />
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{r.name}</p>
                    <p className="text-gray-500 text-xs">{r.brand}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="relative bg-stone-950 py-24 sm:py-36 px-4 sm:px-6 overflow-hidden text-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 sm:w-[500px] h-72 sm:h-[500px] bg-rose-500/20 blur-[100px] rounded-full pointer-events-none" />
        <div className="relative z-10">
          <p className="text-rose-400 text-xs font-medium uppercase tracking-[0.4em] mb-4">Limited Time</p>
          <h2 className="text-white text-6xl sm:text-8xl md:text-9xl font-medium tracking-tight leading-none mb-6">START<br />FREE.</h2>
          <p className="text-gray-400 text-base sm:text-lg mb-10">No credit card required. Your first 30 days, on us.</p>
          <Link to="/products#DemoForm">
            <button className="bg-white text-black font-medium px-10 sm:px-14 py-4 sm:py-5 rounded-full text-base sm:text-lg hover:bg-rose-500 hover:text-white transition-all shadow-2xl">
              Launch Your Store
            </button>
          </Link>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 sm:py-20 bg-stone-50 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-medium mb-8 sm:mb-10 text-center">Any questions?</h2>
          <Faq faqs={faqs} />
        </div>
      </section>

      <div className="bg-black text-white"><Footer /></div>
    </div>
  );
}
